import "./Style.css";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  Tab,
  Tabs,
  Typography,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import TuneIcon from "@mui/icons-material/Tune";
import ImageIcon from "@mui/icons-material/Image";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import MediaTabComponent from "../../MediaUploadComponet/MediaTabComponent";
import CustomTextEditor from "./CustomTextEditor";
import QuestionTab from "./BasicTabs/QuestionTab/QuestionTab";
import NumberTab from "./BasicTabs/NumberTab/NumberTab";
import EmailTab from "./BasicTabs/EmailTab/EmailTab";
import MultipleChoiceTab from "./BasicTabs/MultipleChoiceTab/MultipleChoiceTab";
import DynamicQuestionTab from "./BasicTabs/DynamicQuestionTab/DynamicQuestionTab";
import MobileNumberTab from "./BasicTabs/MobileNumberTab/MobileNumberTab";
import SingleChoiceTab from "./BasicTabs/SingleChoiceTab/SingleChoiceTab";
import RatingTab from "./BasicTabs/RatingTab/RatingTab";
import DatePickerTab from "./BasicTabs/DatePickerTab/DatePickerTab";
import TimePickerTab from "./BasicTabs/TimePickerTab/TimePickerTab";
import RangeTab from "./BasicTabs/RangeTab/RangeTab";
import WebsiteTab from "./BasicTabs/WebsiteTab/WebsiteTab";
import LocationTab from "./BasicTabs/LocationTab/LocationTab";
import FileUploadTab from "./BasicTabs/FileUploadTab/FileUploadTab";
import RealTimeSearchTab from "./BasicTabs/RealTimeSearchTab/RealTimeSearchTab";
import CarouselTab from "./BasicTabs/CarouselTab/CarouselTab";
import OrderItemsTab from "./BasicTabs/OrderItemsTab/OrderItemsTab";
import FormTab from "./BasicTabs/FormTab/FormTab";
import AskContactsTab from "./BasicTabs/AskContactsTab/AskContactsTab";
import AppointmentBookingTab from "./BasicTabs/AppointmentBookingTab/AppointmentBookingTab";
import AuthenticatorTab from "./BasicTabs/AuthenticatorTab/AuthenticatorTab";

const typeLabels = {
  question: "Question",
  single_choice: "Single Choice",
  email_feild: "Email",
  multiple_options: "Multiple Choice",
  mobile_number: "Mobile Number",
  number: "Number",
  rating: "Rating",
  date_picker: "Date Picker",
  time_picker: "Time Picker",
  location: "Location",
  file_upload: "File Upload",
  website: "Website",
  ask_contacts: "Ask Contacts",
  order_items: "Order Items",
  authenticator: "Authenticator",
  form: "Form",
  carousel_with_buttons: "Carousel",
  dynamic_question: "Dynamic",
  Real_time_search: "Real Time Search",
  appointment_booking: "Appointment",
};

const typeColors = {
  question: "#6366f1",
  single_choice: "#8b5cf6",
  email_feild: "#3b82f6",
  multiple_options: "#a855f7",
  mobile_number: "#06b6d4",
  number: "#14b8a6",
  rating: "#f59e0b",
  date_picker: "#ef4444",
  time_picker: "#f97316",
  location: "#84cc16",
  file_upload: "#10b981",
  website: "#6366f1",
  ask_contacts: "#ec4899",
  order_items: "#8b5cf6",
  authenticator: "#06b6d4",
  form: "#14b8a6",
  carousel_with_buttons: "#f59e0b",
  dynamic_question: "#ef4444",
  Real_time_search: "#3b82f6",
  appointment_booking: "#10b981",
};


const EditQuestionPopup = ({
  openEdit,
  handleCloseEdit,
  editingItem,
  onUpdate,
  allQuestions = [],
  initialTabIndex = 0,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [text, setText] = useState("");
  const [options, setOptions] = useState([]);
  const [flexDirection, setFlexDirection] = useState("column");
  const [media, setMedia] = useState("");
  const [skipOption, setSkipOption] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validations, setValidations] = useState({});
  const [style, setStyle] = useState("button");
  const [defaultSelected, setDefaultSelected] = useState(null);
  const [shuffleOptions, setShuffleOptions] = useState(false);
  const [otherOption, setOtherOption] = useState(false);
  const [imageChoices, setImageChoices] = useState([]);
  const [isUserName, setIsUserName] = useState(false);
  const [settings, setSettings] = useState({});
  const [conditions, setConditions] = useState([]);
  const [botSettings, setBotSettings] = useState({});

  useEffect(() => {
    if (editingItem) {
      setText(editingItem.text || "");
      setOptions(editingItem.options);
      setFlexDirection(editingItem.flexDirection || "column");
      setSkipOption(editingItem.skipOption || false);
      setMedia(editingItem.media || "");
      setErrorMessage(editingItem.errorMessage || "");
      setValidations(editingItem.validations || {});
      setStyle(editingItem.style || "button");
      setDefaultSelected(editingItem.defaultSelected ?? null);
      setShuffleOptions(editingItem.shuffleOptions || false);
      setOtherOption(editingItem.otherOption || false);
      setImageChoices(editingItem.imageChoices || []);
      setIsUserName(editingItem.isUserName || false);
      setSettings(editingItem.settings || {});
      setConditions(editingItem.conditions || []);
      setTabIndex(initialTabIndex);
    }
  }, [editingItem]);

  useEffect(() => {
    if (openEdit) {
      const botId = localStorage.getItem("selectedBotId");
      if (botId) {
        import("../../../../../api/botApi").then(({ getBotById }) => {
          getBotById(botId).then((data) => {
            if (data?.botSettings) setBotSettings(data.botSettings);
          }).catch(() => {});
        });
      }
    }
  }, [openEdit]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleSwitchChange = (event) => {
    setSkipOption(event.target.checked); // Update skipOption state when switch toggles
  };

  const handleSave = () => {
    // Validate text (check if it's not empty)
    if (text.trim() === "") {
      setErrorMessage("Question text cannot be empty");
      return;
    }

    if (Array.isArray(options)) {
      if (options.some((option) => {
        if (typeof option === "string") return option.trim() === "";
        if (option && typeof option === "object") {
          return !(option.title || option.name || option.label || option.value || "").toString().trim();
        }
        return false;
      })) {
        setErrorMessage("Options cannot be empty");
        return;
      }
    }

    onUpdate({
      ...editingItem,
      text,
      options: options || [],
      skipOption,
      flexDirection,
      media: media || "",
      errorMessage,
      validations,
      style,
      defaultSelected,
      shuffleOptions,
      otherOption,
      imageChoices: imageChoices || [],
      isUserName,
      settings,
      conditions,
    });

    console.log("options - ", options);
    console.log("flexDirection - ", flexDirection);
    console.log("text - ", text);
    console.log("media - ", media);
    console.log("skipOption - ", skipOption);

    handleCloseEdit();
  };

  const typeLabel = typeLabels[editingItem?.type] || "Question";
  const typeColor = typeColors[editingItem?.type] || "#6366f1";

  const themeColors = botSettings.themeColors || {};
  const themeAccent = themeColors.header || themeColors.option || "#6366f1";

  const basicTab = (
    <Box>
      {editingItem?.type === "question" ? (
        <QuestionTab
          skipOption={skipOption}
          setSkipOption={setSkipOption}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          validations={validations}
          setValidations={setValidations}
        />
      ) : editingItem?.type === "single_choice" ? (
        <SingleChoiceTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          options={options} setOptions={setOptions}
          flexDirection={flexDirection} setFlexDirection={setFlexDirection}
          style={style} setStyle={setStyle}
          defaultSelected={defaultSelected} setDefaultSelected={setDefaultSelected}
          shuffleOptions={shuffleOptions} setShuffleOptions={setShuffleOptions}
          otherOption={otherOption} setOtherOption={setOtherOption}
          imageChoices={imageChoices} setImageChoices={setImageChoices}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "email_feild" ? (
        <EmailTab
          skipOption={skipOption}
          setSkipOption={setSkipOption}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          validations={validations}
          setValidations={setValidations}
        />
      ) : editingItem?.type === "mobile_number" ? (
        <MobileNumberTab
          skipOption={skipOption}
          setSkipOption={setSkipOption}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          validations={validations}
          setValidations={setValidations}
          settings={settings}
          setSettings={setSettings}
        />
      ) : editingItem?.type === "number" ? (
        <NumberTab
          skipOption={skipOption}
          setSkipOption={setSkipOption}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          validations={validations}
          setValidations={setValidations}
        />
      ) : editingItem?.type === "rating" ? (
        <RatingTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
        />
      ) : editingItem?.type === "date_picker" ? (
        <DatePickerTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
        />
      ) : editingItem?.type === "time_picker" ? (
        <TimePickerTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
        />
      ) : editingItem?.type === "range" ? (
        <RangeTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
        />
      ) : editingItem?.type === "website" ? (
        <WebsiteTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
        />
      ) : editingItem?.type === "location" ? (
        <LocationTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          validations={validations} setValidations={setValidations}
        />
      ) : editingItem?.type === "file_upload" ? (
        <FileUploadTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
        />
      ) : editingItem?.type === "multiple_options" ? (
        <MultipleChoiceTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          validations={validations} setValidations={setValidations}
          options={options} setOptions={setOptions}
          style={style} setStyle={setStyle}
          flexDirection={flexDirection} setFlexDirection={setFlexDirection}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "dynamic_question" ? (
        <DynamicQuestionTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          settings={settings} setSettings={setSettings}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "Real_time_search" ? (
        <RealTimeSearchTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "carousel_with_buttons" ? (
        <CarouselTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          options={options} setOptions={setOptions}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "order_items" ? (
        <OrderItemsTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          options={options} setOptions={setOptions}
          validations={validations} setValidations={setValidations}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "form" ? (
        <FormTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "ask_contacts" ? (
        <AskContactsTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "appointment_booking" ? (
        <AppointmentBookingTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
          accentColor={themeAccent}
        />
      ) : editingItem?.type === "authenticator" ? (
        <AuthenticatorTab
          skipOption={skipOption} setSkipOption={setSkipOption}
          errorMessage={errorMessage} setErrorMessage={setErrorMessage}
          settings={settings} setSettings={setSettings}
          accentColor={themeAccent}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
          <Switch checked={skipOption} onChange={handleSwitchChange} />
          <Typography sx={{ fontWeight: 500, color: "#6b7280" }}>
            {skipOption ? "Give Skip Option" : "Do Not Give Skip Option"}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={openEdit}
      onClose={handleCloseEdit}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${themeAccent}15, ${themeAccent}08)`,
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${typeColor}, ${typeColor}cc)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 18,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {typeLabel.charAt(0)}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.3 }}>
                Edit {typeLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                Press "Enter" for paragraph break
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              px: 1.5,
              py: 0.4,
              borderRadius: "20px",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: typeColor,
              bgcolor: `${typeColor}15`,
              border: `1px solid ${typeColor}30`,
            }}
          >
            {typeLabel}
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Text Editor */}
      <Box sx={{ px: 3, pt: 2.5 }}>
        <CustomTextEditor value={text} onChange={setText} />
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 3, mt: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{
            minHeight: 40,
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              bgcolor: themeAccent,
            },
            "& .MuiTab-root": {
              minHeight: 40,
              py: 0.5,
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              "&.Mui-selected": { color: themeAccent, fontWeight: 600 },
            },
          }}
        >
          <Tab icon={<TuneIcon sx={{ fontSize: 18 }} />} label="Basic" iconPosition="start" />
          <Tab icon={<ImageIcon sx={{ fontSize: 18 }} />} label="Media" iconPosition="start" />
          <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} label="Advanced" iconPosition="start" />
          <Tab icon={<CallSplitIcon sx={{ fontSize: 18 }} />} label="Conditions" iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <DialogContent sx={{ px: 3, pt: 1, pb: 1 }}>
        {tabIndex === 0 && basicTab}
        {tabIndex === 1 && (
          <MediaTabComponent media={media} setMedia={setMedia} />
        )}
        {tabIndex === 2 && (
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, bgcolor: isUserName ? "#f0fdf4" : "#f9fafb", borderRadius: 2, border: isUserName ? "1px solid #bbf7d0" : "1px solid #e5e7eb" }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>Mark as Name Field</Typography>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.3 }}>This answer will be used as the visitor's name in leads & notifications</Typography>
              </Box>
              <Switch checked={isUserName} onChange={(e) => setIsUserName(e.target.checked)} color="success" />
            </Box>

            {/* Data Referencing */}
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1f2937", mb: 0.5 }}>Data Referencing</Typography>
              <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 1.5 }}>Insert these variables in your question text to use previous answers</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                {["{name}", "{email}", "{phone}", "{Q1}", "{Q2}", "{Q3}"].map(v => (
                  <Box key={v} onClick={() => { setText(prev => prev + " " + v); }}
                    sx={{ px: 1.5, py: 0.5, bgcolor: "#ede9fe", color: "#6d28d9", borderRadius: 1.5, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px solid #ddd6fe", "&:hover": { bgcolor: "#ddd6fe" } }}>
                    {v}
                  </Box>
                ))}
              </Box>
              <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 1.5 }}>Click to insert into question text. {"{Q1}"} = answer to 1st question, {"{Q2}"} = 2nd, etc.</Typography>
            </Box>
          </Box>
        )}
        {tabIndex === 3 && (
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, border: "1px solid #bae6fd" }}>
              <Typography sx={{ fontSize: 12, color: "#0369a1" }}>
                Add conditions: if visitor selects a specific answer, jump to a different question.
              </Typography>
            </Box>
            {conditions.map((cond, i) => (
              <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", p: 1.5, bgcolor: "#f9fafb", borderRadius: 2, border: "1px solid #e5e7eb" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#6b7280", whiteSpace: "nowrap" }}>If answer =</Typography>
                {options && options.length > 0 ? (
                  <select value={cond.answer} onChange={e => { const c = [...conditions]; c[i] = { ...c[i], answer: e.target.value }; setConditions(c); }}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, background: '#fff' }}>
                    <option value="">Select answer</option>
                    <option value="*">Any answer (*)</option>
                    {options.map((o, oi) => {
                      const label = (typeof o === 'string' ? o : (o.title || o.name || o.label || o.value || '')).toString();
                      return <option key={oi} value={label}>{label || `Option ${oi + 1}`}</option>;
                    })}
                  </select>
                ) : (
                  <TextField size="small" value={cond.answer} placeholder="answer text"
                    onChange={e => { const c = [...conditions]; c[i] = { ...c[i], answer: e.target.value }; setConditions(c); }}
                    sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } }} />
                )}
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#6b7280", whiteSpace: "nowrap" }}>→ Jump to</Typography>
                <select value={cond.jumpTo} onChange={e => { const c = [...conditions]; c[i] = { ...c[i], jumpTo: e.target.value }; setConditions(c); }}
                  style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, background: '#fff' }}>
                  <option value="">Select question</option>
                  {allQuestions.filter(q => q.id !== editingItem?.id).map((q, idx) => (
                    <option key={q.id} value={q.id}>Q{idx + 1}: {(q.text || '').replace(/<[^>]+>/g, '').slice(0, 30)}</option>
                  ))}
                </select>
                <IconButton size="small" onClick={() => setConditions(conditions.filter((_, idx) => idx !== i))} sx={{ color: "#ef4444" }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
            <Button size="small" onClick={() => setConditions([...conditions, { answer: "", jumpTo: "" }])}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontSize: 12, fontWeight: 600, color: themeAccent }}>
              + Add Condition
            </Button>
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleCloseEdit}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            background: `linear-gradient(135deg, ${themeAccent}, ${themeAccent}dd)`,
            boxShadow: `0 4px 15px ${themeAccent}40`,
            "&:hover": {
              background: `linear-gradient(135deg, ${themeAccent}dd, ${themeAccent})`,
              boxShadow: `0 6px 20px ${themeAccent}60`,
            },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditQuestionPopup;
